import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Language, LANGUAGES, LANGUAGES_LIST } from '@/shared/config';
import { cn, useLang } from '@/shared/libs';

const LANGUAGE_SHORT_LABEL: Record<Language, string> = {
  [Language.English]: 'EN',
  [Language.Russian]: 'RU',
};

const LANGUAGE_ARIA_KEY: Record<Language, 'language.en' | 'language.ru'> = {
  [Language.English]: 'language.en',
  [Language.Russian]: 'language.ru',
};

export interface LanguageSwitcherProps {
  className?: string;
}

export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ className }) => {
  const { t } = useTranslation();
  const { changeLang, lang } = useLang();

  return (
    <div
      aria-label={t('language.switch')}
      className={cn(
        'inline-flex w-full rounded-xl bg-foreground/5 p-1',
        className,
      )}
      role="group">
      {LANGUAGES.map((value) => {
        const isActive = lang === value;
        const FlagIcon = LANGUAGES_LIST[value].icon;

        return (
          <button
            key={value}
            aria-label={t(LANGUAGE_ARIA_KEY[value])}
            aria-pressed={isActive}
            className={cn(
              'flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold tracking-wide transition-all',
              isActive
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            type="button"
            onClick={() => changeLang(value)}>
            <FlagIcon className="h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] shadow-sm ring-1 ring-border/50" />
            {LANGUAGE_SHORT_LABEL[value]}
          </button>
        );
      })}
    </div>
  );
};

LanguageSwitcher.displayName = 'LanguageSwitcher';
